<template>
    <footer class="footer" id="footer">
        <div class="container">
            <div class="footer__content">
                <div class="footer__content--row">
                    <Column v-for="(column, index) in columns" :column="column" :key="index" />
                </div>
                <div v-if="developer?.company_year || developer?.active_developer" class="footer__content--copyright-block">
                    <div v-if="!developer?.hide_year" class="footer__content--copyright-block-copyright">
                        © {{ developer?.company_year || '2013' }} – {{ new Date().getFullYear() }}
                        {{ developer?.company_name || 'ECOM' }}. {{ $t('footerTextAttributeAllRightsRezerved') }}
                    </div>
                    <div
                        class="footer__content--copyright-block-developer"
                        v-if="developer?.link || developer?.image || developer?.text"
                    >
                        <template v-if="!developer?.image">
                            {{ $t('footerTextAttributePoweredBy') }}
                        </template>

                        <a v-if="developer?.link || developer?.text" :href="developer?.link" target="_blank">
                            <img v-if="developer?.image" :src="developer?.image" :alt="developer?.text" />
                            <span v-else>{{ developer?.text }}</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>

<script>
import { mapGetters } from 'vuex';
import { createParentCategoryItem } from '~/themes/novaporta/types/categories';
import generateDefImageIcon from '~/mixins/generate-def-image-icon';

export default {
    mixins: [generateDefImageIcon],
    name: 'Footer',
    components: {
        Column: () => import('./components/Column')
    },
    computed: {
        ...mapGetters({
            globalSettings: 'global/globalSettings',
            footerSettings: 'footer/footerSettings',
            mainPhoneNumber: 'global/mainPhoneNumber',
            contacts: 'the-contacts/getShopsLocations',
            developer: 'footer/getDeveloper',
            categories: 'categories/getCategories'
        }),
        generateFooterColumns() {
            return (
                this.footerSettings?.columns?.map((column, index) => {
                    return {
                        title: column.name,
                        items: column.items.map(item => {
                            return {
                                id: item.id,
                                type: item.link && item.link.includes('http') ? 'tagLink' : 'link',
                                label: item.name,
                                link: item.link
                            };
                        }),
                        paymentDetails:
                            index === 1 && this.globalSettings?.payment_types?.length ? this.globalSettings.payment_types : []
                    };
                }) || []
            );
        },
        parentCategories() {
            return this.categories.map(c => {
                return createParentCategoryItem({
                    name: c.categoryTitle,
                    route: `/${c.categoryUrl}`,
                    img: c.categoryIcon
                });
            });
        },
        projectName() {
            return this.globalSettings.project;
        },

        columns() {
            return [this.companyTermsColumn, ...this.generateFooterColumns, this.contactColumn];
        },
        getDevelopersInfo() {
            return this.footerSettings?.developers || [];
        },
        companyTermsColumn() {
            let items = [];

            if (this.globalSettings?.logo?.logoLight) {
                items.push({
                    id: 1,
                    type: 'link',
                    link: '/',
                    image: this.globalSettings?.logo?.logoLight,
                    classes: 'logo'
                });
            }
            // add the conditions
            items.push({
                id: 2,
                type: 'link',
                label: this.$t('globalRoutesTerms'),
                link: '/terms'
            });

            // add the conditions
            items.push({
                id: 3,
                type: 'link',
                label: this.$t('globalRoutesPolitics'),
                link: '/policy'
            });

            return { items };
        },
        contactColumn() {
            const { socialNetworkLinks, phones } = this.globalSettings;
            const { issuanceOfOnlineOrders, workingTime } = this.footerSettings || {};
            const contacts = this.contacts;
            const firstContact = Array.isArray(this.contacts) && this.contacts.length > 0 ? this.contacts[0] : null;

            let itemsContact = [];
            let subItemsSocialMedia = [];

            if (phones?.mainPhoneNumber) {
                itemsContact.push({
                    id: 1,
                    type: 'tagLink',
                    label: phones?.mainPhoneNumber,
                    link: 'tel:' + phones?.mainPhoneNumber,
                    classes: 'phone'
                });
            }

            if (issuanceOfOnlineOrders) {
                itemsContact.push({
                    id: 2,
                    type: 'text',
                    label: issuanceOfOnlineOrders || contacts[0]?.address,
                    classes: 'address'
                });
            }

            if (workingTime || firstContact?.schedule?.length) {
                const schedule = workingTime ? this.schedule : firstContact.schedule.join('<br>');
                itemsContact.push({
                    id: 3,
                    type: 'html',
                    html: schedule,
                    classes: 'schedule'
                });
            }

            if (phones?.mainPhoneNumber) {
                itemsContact.push({
                    id: 4,
                    type: 'link',
                    label: this.$t('footerColumnContactsActionLookToOurStores'),
                    link: '/contacts'
                });
            }

            if (socialNetworkLinks && Object.keys(socialNetworkLinks).length > 0) {
                this?.globalSettings?.social_media?.forEach((item, idx) => {
                    this.addSocialNetworkLink(
                        subItemsSocialMedia,
                        item?.link,
                        item?.icon || this.generateDefImg(item?.key?.slug),
                        item?.key?.slug[0].toUpperCase() + item?.key?.slug?.slice(1),
                        idx + 1
                    );
                });

                itemsContact.push({
                    id: 5,
                    type: 'enumIcons',
                    classes: 'social-media',
                    subItems: subItemsSocialMedia
                });
            }

            return {
                title: this.footerSettings?.buttons?.contactBlockName
                    ? this.footerSettings?.buttons?.contactBlockName
                    : this.$t('footerColumnContactsTitle'),
                items: itemsContact
            };
        },
        schedule() {
            if (this.footerSettings?.workingTime) {
                return Object.values(this.footerSettings.workingTime).join('<br>');
            }

            return null;
        }
    },
    methods: {
        addSocialNetworkLink(subItems, link, icon, label, id) {
            if (link) {
                subItems.push({
                    id: id,
                    image: icon,
                    local: true,
                    link: link,
                    label: label
                });
            }
        }
    },
    async fetch() {
        // if (this.footerSettings === null) {
        await this.$store.dispatch('footer/fetchFooterSettings');
        // }

        // if (this.footerSettings === null || !!this.contacts?.length) {
        await this.$store.dispatch('the-contact/fetchShopsLocations');
        // }
    }
};
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
